import React from 'react'
import HrefLangHead from '@toasttab/shared/src/layout/HrefLangHead'
import { useSiteMetadata } from '@toasttab/main/src/hooks/use-site-metadata'
import { SEOProps } from './SEO.d'
import applicationId from '../../../data/region/us/applicationId.json'
import {
  isLocaleHiddenFromSEO,
  isUrlHiddenFromSEO,
  filterVisibleEntriesForSEO
} from '@toasttab/shared/src/config/localeConfig'

const SEO = ({
  canonical,
  children,
  description,
  language,
  localizedEntries,
  noIndex = false,
  pathname,
  title,
  fullUrl
}: SEOProps) => {
  const {
    title: defaultTitle,
    description: defaultDescription,
    siteUrl: defaultSiteUrl,
    image,
    twitterUsername,
    hostName
  } = useSiteMetadata()

  // Automatically prevent indexing for hidden locales (always in both preview and production)
  const isHiddenLocale =
    isLocaleHiddenFromSEO(language) || isUrlHiddenFromSEO(fullUrl)
  const shouldNoIndex = noIndex || isHiddenLocale

  const seo = {
    title: title || defaultTitle,
    description: description || defaultDescription,
    url: `${defaultSiteUrl}${fullUrl}`.replace(/\/+$/, ''),
    image,
    twitterUsername
  }

  const renderApplicationId = () => {
    return (
      <script type='application/ld+json'>
        {JSON.stringify(applicationId)}
      </script>
    )
  }

  const renderLocalizationHrefLangs = () => {
    // Filter out hidden locale entries from hreflang tags (always hidden in both preview and production)
    const filteredEntries = filterVisibleEntriesForSEO(localizedEntries)

    return (
      <>
        {filteredEntries && filteredEntries?.length > 0 ? (
          <HrefLangHead
            canonical={canonical ? `${defaultSiteUrl}${canonical}` : seo.url}
            currentLanguage={language}
            defaultSiteUrl={defaultSiteUrl}
            localizedEntries={filteredEntries}
          />
        ) : null}
      </>
    )
  }

  return (
    <>
      <title>{seo.title}</title>
      <meta content='width=device-width, initial-scale=1' name='viewport' />
      <meta
        content={description ? description : seo.description}
        name='description'
      />
      <meta
        content={shouldNoIndex ? 'noindex, nofollow' : 'index, follow'}
        name='robots'
      />
      <meta content={seo.url} property='og:url' />
      <meta content={title} property='og:title' />
      <meta
        content='https://d2w1ef2ao9g8r9.cloudfront.net/images/otl.jpg'
        property='og:image'
      />
      <meta content='summary' name='twitter:card' />
      <meta content={seo.twitterUsername} name='twitter:site' />
      <meta content={seo.title} name='twitter:title' />
      <meta
        content={description ? description : seo.description}
        name='twitter:description'
      />
      <meta content={seo.image} name='twitter:image' />
      <meta content={seo.twitterUsername} name='twitter:creator' />
      <link
        href='https://d2w1ef2ao9g8r9.cloudfront.net/images/icons/favicon.ico'
        rel='icon'
      />
      <link
        href={canonical ? `${defaultSiteUrl}${canonical}` : seo.url}
        rel='canonical'
      />
      <meta
        content='LH_mX7y0DJk5nUgUOTzglaHQ0c9EEm5AtHam273KA0I'
        name='google-site-verification'
      />
      <meta
        content='8fmbcvv517c7bmps9diwnv3uemb3v7'
        name='facebook-domain-verification'
      />
      {renderApplicationId()}
      {renderLocalizationHrefLangs()}
      {children}
    </>
  )
}

export default SEO
